<template>
  <div v-if="hasPaymentHistory">
    <content-box class="remain-pv--wrap">
      <p class="content__title">{{ $t('userInfo.managePlan.remainingPvChart.title') }}</p>
      <div class="date-range-picker--wrap">
        <input
          type="text"
          class="periods_label"
          readonly
          :disabled="false"
          :value="dateRangeStr"
          @click.stop="togglePickerClick"
        >
        <date-range-picker
          v-if="showDateRangePicker"
          pickerType="pvHistory"
          :minDate="minDate"
          :maxDate="maxDate"
          :dateRange="dateRange"
          :timezone="getTimezone"
          @onRangeCancel="onRangeCancel"
          @onRangeChange="onRangeChange"
        />
        <i class="icon icon--calendar"></i>
      </div>
      <scroll-content>
        <app-chart v-if="pvStatus.length" chartType="line"
                  :chartOptions="chartOptions"
                  :height="190" />
        <div v-else
          class="no-data">
          <p v-if="!isLoading">
            {{ $t('common.noRecord')}}
          </p>
        </div>
      </scroll-content>
    </content-box>

    <content-box v-if="pvStatus.length" class="remain-pv-table--wrap">
      <div class="table--wrap" ref="listTable">
        <app-pagination
          class="clearfix"
          :total="pvStatusCount"
          :page="page"
          :limit="limit"
          @onPagingOptionChange="onPagingOptionChange" />

        <data-table class="pv-detail"
          :headers="headers">
          <template v-slot:colgroup>
            <colgroup>
              <col width="25%">
              <col width="25%">
              <col width="25%">
              <col width="25%">
            </colgroup>
          </template>
          <template v-slot>
            <tbody>
            <tr v-for="(item, index) in pvStatusList" :key="index">
              <td class="data-date">{{ item.date | dashDate }}</td>
              <td class="data-remain">{{ item.remainPV | thousand }}</td>
              <td class="data-used">{{ item.usePV | thousand }}</td>
              <td class="data-provide">{{ item.chargePV | thousand }}</td>
            </tr>
            </tbody>
          </template>
        </data-table>
      </div>
    </content-box>
  </div>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import DateRangePicker from '@/components/date-picker/DateRangePicker'
import AppPagination from '@/components/ui/AppPagination'
import DataTable from '@/components/ui/DataTable'

import { mapState } from 'vuex'
import { parse, format, startOfDay, addDays } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import { getPvStatus } from '@/api/user'
import { getTooltipHtml } from '@/libs/chart-helper'
import { thousand, getLocaleDateStr, tzOffsetDate } from '@/libs/utils'
import AppChart from '@/components/chart'
import ScrollContent from '@/components/ui/ScrollContent'

export default {
  components: {
    ContentBox,
    DateRangePicker,
    AppChart,
    ScrollContent,
    AppPagination,
    DataTable
  },
  props: [
    'startDate'
  ],
  data () {
    return {
      isLoading: true,
      hasPaymentHistory: false,
      minDate: null,
      maxDate: null,
      dateRange: {
        startDate: null,
        endDate: null
      },
      showDateRangePicker: false,
      // pv table
      headers: [
        {
          name: this.$t('common.table.header.date')
        },
        {
          name: this.$t('common.table.header.remaingPV')
        },
        {
          name: this.$t('common.table.header.userPV')
        },
        {
          name: this.$t('common.table.header.providedPV')
        }
      ],
      page: 1,
      limit: 30,
      pvStatus: []
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData'
    ]),
    pvStatusCount () {
      return this.pvStatus ? this.pvStatus.length : 0
    },
    pvStatusList () {
      if (!this.pvStatus) {
        return []
      }
      const startIndex = (this.page - 1) * this.limit
      const endIndex = startIndex + this.limit
      return this.pvStatus.slice(startIndex, endIndex)
    },
    dateRangeStr () {
      return `${getLocaleDateStr(this.dateRange.startDate)} - ${getLocaleDateStr(this.dateRange.endDate)}`
    },
    chartOptions () {
      const pvData = this.pvStatus.map(item => {
        return [
          format(parse(item.date), 'YYYY-MM-DD'),
          item.remainPV > 0 ? item.remainPV : 0
        ]
      })

      const options = {
        meta: {
          isDailyChart: true
        },
        grid: {
          left: 10,
          right: 35
        },
        series: [{
          name: 'PV',
          type: 'line',
          data: pvData,
          showSymbol: false,
          symbolSize: 2,
          smooth: true
        }],
        tooltip: {
          formatter (params) {
            const item = params[0]
            const title = format(parse(item.axisValue), 'YYYY-MM-DD')
            let seriesName = item.seriesName
            let value = item.value[1]
            const content = [
              [
                `${item.marker}${seriesName} : `,
                thousand(value)
              ]
            ]
            return getTooltipHtml(content, title)
          }
        },
        xAxis: {
          // type: 'category',
          boundaryGap: false,
          axisLabel: {
            formatter (value) {
              return format(parse(value), 'YYYY-MM-DD')
            }
          }
        },
        yAxis: {
          type: 'value',
          splitNumber: 2,
          axisLabel: {}
        },
        color: '#EC0047'
      }
      return options
    },
    getTimezone () {
      return `(${this.userData.timezone}) ${this.userData.timezoneName}`
    }
  },
  created () {
    const today = startOfDay(tzOffsetDate(new Date(), this.userData.timezone))
    const minDate = startOfDay(tzOffsetDate(parse(this.userData.regDate)))
    const maxDate = parse(today)
    const endDate = parse(today)
    let startDate = addDays(today, -6)

    if (startDate < minDate) {
      startDate = parse(minDate)
    }

    this.hasPaymentHistory = true
    this.minDate = minDate
    this.maxDate = maxDate

    this.dateRange = {
      startDate,
      endDate
    }
    this.loadData()
  },
  mounted () {
    EventBus.$on('closeDateRangePicker', () => {
      this.showDateRangePicker = false
    })
  },
  methods: {
    loadData () {
      this.isLoading = true
      const startDate = this.dateRange.startDate
      const endDate = this.dateRange.endDate

      if (startDate && endDate) {
        const start = format(startDate, 'YYYY-MM-DD')
        const end = format(endDate, 'YYYY-MM-DD')
        getPvStatus(start, end).then(data => {
          this.pvStatus = data
          this.isLoading = false
        }).catch(() => {
          this.pvStatus = []
          this.isLoading = false
        })
      } else {
        this.pvStatus = []
        this.isLoading = false
      }
    },
    togglePickerClick () {
      EventBus.$emit('closeAllUiSelectBox')
      EventBus.$emit('closeUserMenu')
      EventBus.$emit('closeReferrerList')
      EventBus.$emit('closeReportPageList')
      this.showDateRangePicker = !this.showDateRangePicker
    },
    closePicker () {
      this.showDateRangePicker = false
    },
    onRangeCancel () {
      this.closePicker()
    },
    onRangeChange (data) {
      this.dateRange = data.range
      this.loadData()
      this.closePicker()
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.remain-pv--wrap,
.remain-pv-table--wrap {
  padding: 25px;
  box-sizing: border-box;
  margin-top: 30px;
}

.remain-pv--wrap {
  .content__title {
    font-size: 15px;
    font-weight: bold;
    color: #2f2f2f;
    display: inline-block;
  }

  .no-data {
    height: 100px;
    position: relative;
    &> p {
      text-align: center;
      @include positionYcenter;
    }
  }

  .date-range-picker--wrap {
    position: relative;
    display: inline-block;
    margin-left: 10px;
    input {
      position: relative;
      width: 215px;
      height: 30px;
      border-radius: 8px;
      border: solid 1px #e0e0e0;
      background-color: #ffffff;
      padding-left: 10px;
      color: #767676;
    }
    .icon--calendar {
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      pointer-events: none;
    }
    /deep/ .date_range_picker {
      top: -20px;
      left: 520px;
    }
  }
}

.remain-pv-table--wrap {
  .content {
    text-align: center;
  }

  .table--wrap {
    text-align: center;

    tbody tr {
      border-bottom: 1px solid #ebebeb;

      td {
        height: 50px;
        font-size: 13px;
        line-height: 1.23;
        color: #2f2f2f;
      }
    }
  }
}
</style>
